import { useMemo, useCallback } from "react";
import classNames from "classnames";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterBorderColor,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";

/** Tema 2 admin sayfa inset sınıfları — `_sidebar-and-main-panel.scss` ile eşleşmeli */
export const THEME2_ADMIN_INSETS = {
  flags: "theme2-flags-inset",
  roles: "theme2-roles-inset",
  inventoryParameters: "theme2-inventory-parameters-inset",
  portfolios: "theme2-portfolios-inset",
  channels: "theme2-channels-inset",
  institution: "theme2-institution-inset",
  communicationTemplates: "theme2-communication-templates-inset",
  bulkMessaging: "theme2-bulk-messaging-inset",
  emailSettings: "theme2-email-settings-inset",
  paymentSettings: "theme2-email-settings-inset",
  apiPermissions: "theme2-api-permissions-inset",
  users: "theme2-users-inset",
  changePassword: "theme2-change-password-inset",
  tenants: "theme2-tenants-inset",
};

/**
 * Tema 2 form sayfaları — tek kaynak: scope CSS, content/form sınıfları, input/select stilleri.
 *
 * @param {string} pageKey — örn. "flags-add" → .flags-add-page, .flags-add-form-fields
 * @param {string} [insetClass] — THEME2_ADMIN_INSETS veya özel inset
 * @param {boolean} [withSelectStyles=false] — react-select border stilleri
 * @param {object} [activeColorStyle] — withSelectStyles için gerekli
 */
export function useTheme2FormPage({
  shellThemeVariant = "1",
  activeColorStyle,
  pageKey,
  insetClass,
  withSelectStyles = false,
  rootClassName = "content",
  compact = true,
}) {
  const isTheme2 = shellThemeVariant === "2";
  const pageClass = `${pageKey}-page`;
  const pageTheme2Class = `${pageKey}-page--theme2`;
  const formFieldsClass = `${pageKey}-form-fields`;

  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: `.${pageClass}.${pageTheme2Class}`,
            fieldsSelector: `.${formFieldsClass}`,
          })
        : "",
    [isTheme2, pageClass, pageTheme2Class, formFieldsClass]
  );

  const selectStyles = useMemo(
    () =>
      withSelectStyles && activeColorStyle
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2)
        : undefined,
    [activeColorStyle, isTheme2, withSelectStyles]
  );

  const contentClassName = useMemo(
    () =>
      classNames(rootClassName, pageClass, {
        [pageTheme2Class]: isTheme2,
        "theme2-admin-content-compact": isTheme2 && compact,
        ...(insetClass ? { [insetClass]: isTheme2 } : {}),
      }),
    [rootClassName, pageClass, pageTheme2Class, isTheme2, compact, insetClass]
  );

  const formFieldsClassName = classNames({ [formFieldsClass]: isTheme2 });

  const inputStyle = useMemo(
    () => (isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined),
    [isTheme2]
  );

  const theme2BorderColor = useMemo(
    () => (isTheme2 ? getTheme2SearchFilterBorderColor() : "#ced4da"),
    [isTheme2]
  );

  const inputClassName = useCallback(
    (invalid) => classNames("form-control", { "is-invalid": invalid }),
    []
  );

  return {
    isTheme2,
    inputStyle,
    inputClassName,
    selectStyles,
    theme2FieldsCss,
    theme2BorderColor,
    contentClassName,
    formFieldsClassName,
    formFieldsClass,
    pageClass,
  };
}

/** Tema 2 form-control / react-select scope CSS enjeksiyonu */
export function Theme2FormFieldsStyle({ css }) {
  if (!css) return null;
  return <style>{css}</style>;
}
